'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useFirebaseUser } from '@/platform/lib/useFirebaseUser';
import { getFirebaseAuth } from '@/platform/lib/firebase';
import { isAllowedOpsAuthReturn } from '@/platform/lib/authDomain';
import { consumeHashIdToken, rememberOpsAuthReturn } from '@/platform/lib/idTokenStore';
import { needsTermsAcceptance, type UserProfile } from '@/platform/users/api';
import {
  establishSession,
  softClearSessionCookie,
  SessionUidMismatchError,
  TermsRequiredError,
} from './establishSession';
import { JoinGateError, joinGateFromUnknown, syncProfileWithJoinGate } from './joinGate';

type AuthContextValue = {
  user: ReturnType<typeof useFirebaseUser>;
  profile: UserProfile | null;
  isAuthenticated: boolean;
  /** Firebase resolved and session bootstrap finished (success or failure). */
  sessionReady: boolean;
  isLoading: boolean;
  needsTermsAcceptance: boolean;
  joinGateError: JoinGateError | null;
  /** Session cookie / profile sync failed after Firebase sign-in. */
  sessionError: string | null;
  refreshProfile: () => Promise<UserProfile | null>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const guestAuthValue: AuthContextValue = {
  user: null,
  profile: null,
  isAuthenticated: false,
  sessionReady: true,
  isLoading: false,
  needsTermsAcceptance: false,
  joinGateError: null,
  sessionError: null,
  refreshProfile: async () => null,
};

/** Marketing tour /embed — satisfies useAuth without Firebase or session APIs. */
export function GuestAuthProvider({ children }: { children: ReactNode }) {
  const value = useMemo(() => guestAuthValue, []);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const user = useFirebaseUser();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [joinGateError, setJoinGateError] = useState<JoinGateError | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(false);
  const tourBuild = process.env.NEXT_PUBLIC_TOUR_BUILD === 'true';
  const [firebaseReady, setFirebaseReady] = useState(
    !!process.env.NEXT_PUBLIC_E2E_MOCK_UID || tourBuild,
  );
  const syncedUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const ret = new URLSearchParams(window.location.search).get('authReturn');
    if (ret && isAllowedOpsAuthReturn(ret)) {
      rememberOpsAuthReturn(ret);
    }
    consumeHashIdToken();
  }, []);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_E2E_MOCK_UID || tourBuild) return;
    let auth;
    try {
      auth = getFirebaseAuth();
    } catch {
      setFirebaseReady(true);
      return;
    }
    const unsub = onAuthStateChanged(auth, () => {
      setFirebaseReady(true);
    });
    return unsub;
  }, [tourBuild]);

  useEffect(() => {
    if (!user?.uid) {
      setProfile(null);
      setJoinGateError(null);
      setSessionError(null);
      syncedUidRef.current = null;
      setSessionReady(true);
      // Drop Hosting cookie when Firebase signs out so the next visitor is not the prior UID.
      if (!process.env.NEXT_PUBLIC_E2E_MOCK_UID && !tourBuild) {
        void softClearSessionCookie();
      }
      return;
    }
    if (syncedUidRef.current === user.uid) {
      setSessionReady(true);
      return;
    }

    let cancelled = false;
    syncedUidRef.current = user.uid;
    setSessionReady(false);

    const sessionBootstrap = process.env.NEXT_PUBLIC_E2E_MOCK_UID
      ? syncProfileWithJoinGate()
      : establishSession();

    void (async () => {
      try {
        const p = await sessionBootstrap;
        if (cancelled) return;
        setJoinGateError(null);
        setSessionError(null);
        setProfile(p);
        syncedUidRef.current = user.uid;
      } catch (err) {
        if (cancelled) return;
        syncedUidRef.current = null;
        const gate = joinGateFromUnknown(err);
        if (gate) {
          setJoinGateError(gate);
          setSessionError(null);
          setProfile(null);
          return;
        }
        if (err instanceof TermsRequiredError) {
          setJoinGateError(null);
          setSessionError(null);
          setProfile(err.profile);
          syncedUidRef.current = user.uid;
          return;
        }
        if (err instanceof SessionUidMismatchError) {
          console.warn('auth: session uid mismatch after bootstrap', err.message);
          setJoinGateError(null);
          setSessionError('Sign-in did not finish. Try again.');
          setProfile(null);
          return;
        }
        // Keep Firebase user; retry profile via Bearer-only sync once.
        try {
          const p = await syncProfileWithJoinGate();
          if (cancelled) return;
          if (p.id && p.id !== user.uid) {
            setJoinGateError(null);
            setSessionError('Sign-in did not finish. Try again.');
            setProfile(null);
            return;
          }
          setJoinGateError(null);
          setSessionError(null);
          setProfile(p);
          syncedUidRef.current = user.uid;
        } catch (retryErr) {
          if (cancelled) return;
          const retryGate = joinGateFromUnknown(retryErr);
          if (retryGate) {
            setJoinGateError(retryGate);
            setSessionError(null);
            setProfile(null);
            return;
          }
          setJoinGateError(null);
          setSessionError(
            retryErr instanceof Error ? retryErr.message : 'Sign-in did not finish. Try again.',
          );
          setProfile(null);
        }
      } finally {
        if (!cancelled) setSessionReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.uid, tourBuild]);

  const refreshProfile = useCallback(async () => {
    if (!user?.uid) {
      setProfile(null);
      return null;
    }
    try {
      const p = await syncProfileWithJoinGate();
      if (p.id && p.id !== user.uid) {
        const established = await establishSession();
        setJoinGateError(null);
        setProfile(established);
        syncedUidRef.current = user.uid;
        return established;
      }
      setJoinGateError(null);
      setProfile(p);
      return p;
    } catch (firstErr) {
      const firstGate = joinGateFromUnknown(firstErr);
      if (firstGate) {
        setJoinGateError(firstGate);
        setProfile(null);
        return null;
      }
      try {
        const p = await establishSession();
        setJoinGateError(null);
        setProfile(p);
        syncedUidRef.current = user.uid;
        return p;
      } catch (err) {
        const gate = joinGateFromUnknown(err);
        if (gate) {
          setJoinGateError(gate);
          setProfile(null);
          return null;
        }
        if (err instanceof TermsRequiredError) {
          setJoinGateError(null);
          setProfile(err.profile);
          return err.profile;
        }
        return null;
      }
    }
  }, [user?.uid]);

  const value = useMemo(
    (): AuthContextValue => ({
      user,
      profile,
      isAuthenticated: !!user,
      sessionReady,
      isLoading: !firebaseReady,
      needsTermsAcceptance: !!user && needsTermsAcceptance(profile),
      joinGateError,
      sessionError,
      refreshProfile,
    }),
    [user, profile, sessionReady, firebaseReady, joinGateError, sessionError, refreshProfile],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
